/*
  CreateModel.jsx
  ----------------
  Interface pour créer et déployer un modèle d'attestations.
*/

import { useEffect, useState } from 'react';
import { Box, Stack, Text, TextInput, Button, ActionIcon, Group, Checkbox, Select, Center, Modal, Loader, Divider, Menu, rem, Textarea, NumberInput } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useUser } from '../tools/Context';
import { IconApi, IconCode, IconDotsVertical, IconFileInfo, IconInfoCircle, IconPlus, IconTrash } from '@tabler/icons-react';
import { post } from '../tools/Request';
import { CodeHighlight } from '@mantine/code-highlight';
import { useNavigate } from 'react-router-dom';

const VariableInput = ({ variable, index, handleVariableChange, removeVariable, setModalListInformation }) => (
  <Group key={index} align="center" justify="center" spacing="xs">
    <TextInput placeholder="Nom de la variable" value={variable.name} onChange={(e) => handleVariableChange(index, 'name', e.target.value)} />
    <TextInput placeholder="Description" value={variable.description} onChange={(e) => handleVariableChange(index, 'description', e.target.value)} />
    <Select
      placeholder="Type"
      value={variable.type}
      data={[
        { value: 'string', label: 'String' },
        { value: 'bool', label: 'Boolean' },
        { value: 'int', label: 'Integer' },
        { value: 'uint', label: 'Unsigned Integer' }
      ]}
      onChange={(value) => handleVariableChange(index, 'type', value)}
    />
    <Checkbox label="Liste" checked={variable.list} onChange={(e) => handleVariableChange(index, 'list', e.target.checked)} />
    <ActionIcon onClick={() => setModalListInformation(true)} ml={-20} mt={-10} size="lg" variant="transparent">
      <IconInfoCircle style={{ width: '60%', height: '60%' }} />
    </ActionIcon>
    <ActionIcon color="red" onClick={() => removeVariable(index)}>
      <IconTrash />
    </ActionIcon>
  </Group>
);

export default function CreateModel() {
  const [modelName, setModelName] = useState('');
  const [variables, setVariables] = useState([{ name: '', description: '', type: 'string', list: false }]);
  const [isDeployEnabled, setIsDeployEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const { refreshPage } = useUser();
  const [modalAPI, setModalAPI] = useState(false);
  const [modalData, setModalData] = useState(false);
  const [modalBatchInformation, setModalBatchInformation] = useState(false);
  const [modalRevocableInformation, setmodalRevocableInformation] = useState(false);
  const [modalEncryptInformation, setmodalEncryptInformation] = useState(false);
  const [modalListInformation, setModalListInformation] = useState(false);
  const [modalMaxHoursInformation, setModalMaxHoursInformation] = useState(false);
  const [requestPreview, setRequestPreview] = useState('');
  const [batchMode, setBatchMode] = useState(false);
  const [revocable, setRevocable] = useState(false);
  const [encrypted, setEncrypted] = useState(false);
  const [maxHours, setMaxHours] = useState(2);
  const navigate = useNavigate();

  const handleVariableChange = (index, field, value) => {
    if (field === 'name' && /\d/.test(value)) return;
    const updatedVariables = [...variables];
    updatedVariables[index][field] = value;
    setVariables(updatedVariables);
    updateDeployState();
  };

  const addVariable = () => {
    setVariables([...variables, { name: '', type: 'string', list: false }]);
    setIsDeployEnabled(false);
  };

  const removeVariable = (index) => {
    const updatedVariables = variables.filter((_, i) => i !== index);
    setVariables(updatedVariables);
    updateDeployState();
  };

  const updateDeployState = () => {
    const allVariablesHaveName = variables.every((variable) => variable.name.trim() !== '');
    setIsDeployEnabled(modelName.trim() !== '' && allVariablesHaveName);
  };

  const generateAndDeploy = async () => {
    const variableNames = variables.map((variable) => variable.name.trim());
    const hasDuplicates = variableNames.some((name, index) => name !== '' && variableNames.indexOf(name) !== index);

    if (hasDuplicates) {
      showNotification({
        title: 'Erreur',
        message: 'Chaque variable doit avoir un nom unique.',
        color: 'red',
        autoClose: 3000
      });
      return;
    }

    setLoading(true);
    try {
      const response = await post(
        'contract/generateAndDeploy',
        {
          modelName,
          variables,
          batch: batchMode,
          revocable,
          encrypted,
          maxHours
        },
        localStorage.getItem('userToken')
      );
      console.log('generateAndDeploy : ', response);
      refreshPage(true);
      refreshPage(false);

      showNotification({
        title: 'Déploiement réussi',
        message: 'Le modèle a été déployé avec succès.',
        color: 'teal',
        autoClose: 3000
      });
      navigate(`/mymodel?select=${encodeURIComponent(modelName)}`);

      setModelName('');
      setVariables([{ name: '', description: '', type: 'string', list: false }]);
      setIsDeployEnabled(false);
    } catch (error) {
      console.error('Error during deployment:', error);
      const message = error?.response?.data?.error || error?.error || error?.message || 'Une erreur inconnue est survenue.';

      showNotification({
        title: 'Erreur de déploiement',
        message: `Erreur : ${message}`,
        color: 'red',
        autoClose: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    try {
      const requestBody = {
        modelName,
        batch: batchMode,
        revocable,
        encrypted,
        maxHours,
        variables
      };

      const requestDetails = {
        url: 'http://localhost:3002/contract/generateAndDeploy',
        method: 'POST',
        headers: {
          Authorization: `Bearer ${'token'}`,
          'Content-Type': 'application/json'
        },
        body: requestBody
      };

      setRequestPreview(JSON.stringify(requestDetails, null, 2));
    } catch {
      setRequestPreview('Invalid JSON');
    }
  }, [modelName, batchMode, revocable, encrypted, variables, maxHours]);

  return (
    <>
      <Center>
        <Box
          mt="5%"
          backgroundColor="white"
          maw={1000}
          style={{
            flex: 1,
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: '1rem',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
          <Stack>
            <Group justify="space-between" align="center" style={{ width: '100%' }}>
              <Text style={{ fontSize: '1.25rem', fontWeight: 500 }}>Créer du modèle</Text>
              <Menu shadow="md" width={200}>
                <Menu.Target>
                  <ActionIcon size="lg" variant="light">
                    <IconDotsVertical style={{ width: '60%', height: '60%' }} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    component="a"
                    href={'/docs/docs/how_to_use_datalock/create_model'}
                    size="lg"
                    target="_blank"
                    variant="transparent"
                    leftSection={<IconFileInfo style={{ width: rem(14), height: rem(14) }} />}>
                    Voir la documentation
                  </Menu.Item>
                  <Menu.Item onClick={() => setModalAPI(true)} leftSection={<IconApi style={{ width: rem(14), height: rem(14) }} />}>
                    Aperçu de la requête
                  </Menu.Item>
                  <Menu.Item onClick={() => setModalData(true)} leftSection={<IconCode style={{ width: rem(14), height: rem(14) }} />}>
                    Aperçu des données
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
            <Divider />
            <Center>
              <TextInput
                w={560}
                placeholder="Nom du modèle"
                value={modelName}
                onChange={(event) => {
                  const value = event.currentTarget.value;
                  if (/\d/.test(value)) return;
                  setModelName(value);
                  updateDeployState();
                }}
              />
            </Center>
            <Group justify="center">
              <Checkbox label="Mode batch" checked={batchMode} onChange={(event) => setBatchMode(event.currentTarget.checked)} />
              <ActionIcon onClick={() => setModalBatchInformation(true)} ml={-20} mt={-10} size="lg" variant="transparent">
                <IconInfoCircle style={{ width: '60%', height: '60%' }} />
              </ActionIcon>
              <Checkbox label="Attestations révocables" checked={revocable} onChange={(event) => setRevocable(event.currentTarget.checked)} />
              <ActionIcon onClick={() => setmodalRevocableInformation(true)} ml={-20} mt={-10} size="lg" variant="transparent">
                <IconInfoCircle style={{ width: '60%', height: '60%' }} />
              </ActionIcon>
              <Checkbox label="Chiffrer les données" checked={encrypted} onChange={(event) => setEncrypted(event.currentTarget.checked)} />
              <ActionIcon onClick={() => setmodalEncryptInformation(true)} ml={-20} mt={-10} size="lg" variant="transparent">
                <IconInfoCircle style={{ width: '60%', height: '60%' }} />
              </ActionIcon>
            </Group>
            <Divider />
            <Group justify="center">
              <NumberInput
                label="Délai maximal d'enregistrement sur la blockchain en heure(s)."
                suffix=" heure"
                value={maxHours}
                onChange={setMaxHours}
                min={1}
                max={168}
                defaultValue={2}
              />
              <ActionIcon onClick={() => setModalMaxHoursInformation(true)} ml={-20} mt={-10} size="lg" variant="transparent">
                <IconInfoCircle style={{ width: '60%', height: '60%' }} />
              </ActionIcon>
            </Group>

            <Divider />
            <Text ml={0}>Définissez vos variables</Text>
            <Box p="md" style={{ border: '1px solid #eaeaea', borderRadius: '8px' }}>
              <Stack spacing="xs">
                {variables.map((variable, index) => (
                  <VariableInput
                    key={index}
                    variable={variable}
                    index={index}
                    handleVariableChange={handleVariableChange}
                    removeVariable={removeVariable}
                    setModalListInformation={setModalListInformation}
                  />
                ))}
                <Center>
                  <Button compact leftIcon={<IconPlus />} variant="light" onClick={addVariable}>
                    Ajouter une variable
                  </Button>
                </Center>
              </Stack>
            </Box>
            <Divider />
            <Text size="xs" c="dimmed">
              *Chaque variable devra être définie lors de la création d'une attestation.
            </Text>
            {encrypted && (
              <Text size="xs" c="dimmed">
                *Uniquement les chaînes de caractères sont chiffrés.
              </Text>
            )}
            <Group justify="center">
              <Button w={212} onClick={generateAndDeploy} color="#0a0082" disabled={!isDeployEnabled}>
                {loading ? <Loader size="xs" color="white" /> : 'Créer'}
              </Button>
            </Group>
          </Stack>
        </Box>
      </Center>
      <Modal opened={modalAPI} onClose={() => setModalAPI(false)} title="Aperçu de la requête" size="lg">
        <CodeHighlight code={requestPreview} language="json" copyLabel="Copier" copiedLabel="Copié!" />
      </Modal>
      <Modal opened={modalData} onClose={() => setModalData(false)} title="Aperçu des données" size="lg">
        <Textarea autosize minRows={14} maxRows={14} h={330} value={JSON.stringify(variables, null, 2)} readOnly />
      </Modal>
      <Modal opened={modalBatchInformation} onClose={() => setModalBatchInformation(false)} title="Mode batch" size="lg">
        <Divider mb={15} />
        <Text autosize>
          Le mode par défaut vous permet d'enregistrer des attestations une par une, le mode batch permet de réduire les coûts si le modèle est destiné à publier plusieurs
          attestations en même temps.
        </Text>
      </Modal>
      <Modal opened={modalRevocableInformation} onClose={() => setmodalRevocableInformation(false)} title="Attestations révocables" size="lg">
        <Divider mb={15} />
        <Text autosize>
          L'activation de l'option "Attestations révocables" permet de modifier le statut d'une attestation afin de pouvoir modifier sa validité après enregistrement.
        </Text>
      </Modal>
      <Modal opened={modalEncryptInformation} onClose={() => setmodalEncryptInformation(false)} title="Chiffrer les données" size="lg">
        <Divider mb={15} />
        <Text autosize>
          Les données d'une attestation seront chiffrées par DataLock à l'aide d'une clef de chiffrement que vous définirez à la création d'une attestation. Elles seront donc
          visibles sur la blockchain mais incompréhensibles si elles ne sont pas déchiffrées. DataLock se charge de les déchiffrer au moment où elles sont demandées.
        </Text>
      </Modal>
      <Modal opened={modalListInformation} onClose={() => setModalListInformation(false)} title="Format liste" size="lg">
        <Divider mb={15} />
        <Text autosize>Si la case est cochée, la donnée sera stockée sous forme de liste.</Text>
      </Modal>
      <Modal opened={modalMaxHoursInformation} onClose={() => setModalMaxHoursInformation(false)} title="Délai maximal d'enregistrement sur la blockchain" size="lg">
        <Divider mb={15} />
        <Text>
          Ce champ définit le temps maximal, en heures, avant qu'une attestation ne soit validée sur la blockchain. Le coût d'enregistrement d'une attestation pouvant fluctuer, un
          délai plus long permet souvent de réduire les frais en créant l'attestation au moment où les coûts sont les plus faibles.
        </Text>
      </Modal>
    </>
  );
}
